import { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { BaseDialog } from '@components/dialog';
import { showModalForm } from '@components/modal-form';
import { showToast } from '@components/toaster';
import {
  captureVoiceNote,
  captureWithWebcam,
  pickDeviceFiles,
  prefersNativeCapture,
} from '@components/media-capture';
import { hasFiles, pastedFiles } from '@components/attachments-tray/attachments';
import { IcoCamera, IcoImage, IcoMicrophone, IcoVideoCamera, IcoX } from '@components/icons';
import { useIntl } from 'shared/intl/use-intl';
import {
  availableCaptures,
  CaptureSource,
  CaptureSupport,
  DEFAULT_SOURCES,
  FilePickerSource,
  isAcceptedFile,
} from './sources';

export type { FilePickerSource } from './sources';

export type PickFilesOpts = {
  sources?: FilePickerSource[];
  accept?: string;
  multiple?: boolean;
};

function captureSupport(): CaptureSupport {
  return {
    camera: typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
    recorder: typeof MediaRecorder !== 'undefined',
  };
}

function openFileDialog(opts: PickFilesOpts) {
  return new Promise<File[] | undefined>((resolve) => {
    pickDeviceFiles({ accept: opts.accept, multiple: opts.multiple }, resolve, () =>
      resolve(undefined),
    );
  });
}

function SourceButton({
  primary,
  onClick,
  children,
}: {
  primary?: boolean;
  onClick(): void;
  children: ComponentChildren;
}) {
  return (
    <button
      type="button"
      class={`inline-flex items-center justify-center gap-2 min-h-10 px-2.5 py-2 rounded-lg border text-sm font-medium whitespace-nowrap ${
        primary
          ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-500'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

type Choice = { files: File[] } | { capture: CaptureSource };

function SourceChooser({
  opts,
  allowFiles,
  captures,
  resolve,
}: {
  opts: PickFilesOpts;
  allowFiles: boolean;
  captures: CaptureSource[];
  resolve(choice?: Choice): void;
}) {
  const intl = useIntl();
  const [isOver, setIsOver] = useState(false);
  const iconClass = 'size-[18px] shrink-0 text-gray-500';

  function takeFiles(files: File[]) {
    if (!allowFiles) {
      return;
    }
    const accepted = files.filter((file) => isAcceptedFile(file, opts.accept));
    if (!accepted.length) {
      showToast({
        type: 'warn',
        title: intl("Files weren't added"),
        message: intl("That type of file isn't supported here."),
      });
      return;
    }
    resolve({ files: opts.multiple ? accepted : accepted.slice(0, 1) });
  }

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const files = pastedFiles(e.clipboardData);
      if (files.length) {
        e.preventDefault();
        takeFiles(files);
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  return (
    <BaseDialog onClose={() => resolve()} contentWidth blurBg={false} compact zIndex="z-[70]">
      <section
        class={`m-4 sm:w-xl max-w-full rounded-xl border-[1.5px] border-dashed p-4 transition-colors ${
          isOver ? 'bg-indigo-50 border-indigo-500' : 'border-gray-300'
        }`}
        aria-labelledby="file-picker-title"
        onDragOver={(e) => {
          if (hasFiles(e)) {
            e.preventDefault();
            setIsOver(true);
          }
        }}
        onDragLeave={(e) => {
          if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) {
            setIsOver(false);
          }
        }}
        onDrop={(e) => {
          setIsOver(false);
          if (!hasFiles(e)) {
            return;
          }
          e.preventDefault();
          takeFiles(Array.from(e.dataTransfer?.files || []));
        }}
      >
        <header class="flex items-start justify-between gap-4">
          <div>
            <h3 id="file-picker-title" class="text-sm font-semibold text-gray-900">
              {intl('Upload or record')}
            </h3>
            <p class="mt-0.5 mb-3 text-sm text-gray-500">{intl('Drag or paste a file here.')}</p>
          </div>
          <button
            type="button"
            class="rounded-md p-1 text-gray-500 hover:bg-gray-100"
            aria-label={intl('Close')}
            onClick={() => resolve()}
          >
            <IcoX class="size-4" />
          </button>
        </header>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {allowFiles && (
            <SourceButton
              primary
              onClick={() =>
                pickDeviceFiles({ accept: opts.accept, multiple: opts.multiple }, takeFiles)
              }
            >
              <IcoImage class="size-[18px] shrink-0" />
              {intl('Choose file')}
            </SourceButton>
          )}
          {captures.includes('takephoto') && (
            <SourceButton onClick={() => resolve({ capture: 'takephoto' })}>
              <IcoCamera class={iconClass} />
              {intl('Take photo')}
            </SourceButton>
          )}
          {captures.includes('takevideo') && (
            <SourceButton onClick={() => resolve({ capture: 'takevideo' })}>
              <IcoVideoCamera class={iconClass} />
              {intl('Record video')}
            </SourceButton>
          )}
          {captures.includes('takeaudio') && (
            <SourceButton onClick={() => resolve({ capture: 'takeaudio' })}>
              <IcoMicrophone class={iconClass} />
              {intl('Record audio')}
            </SourceButton>
          )}
        </div>
      </section>
    </BaseDialog>
  );
}

async function capture(source: CaptureSource) {
  if (source === 'takeaudio') {
    return captureVoiceNote();
  }
  return captureWithWebcam(source === 'takevideo' ? 'video' : 'photo');
}

export async function pickFiles(opts: PickFilesOpts = {}): Promise<File[] | undefined> {
  const sources = opts.sources || DEFAULT_SOURCES;
  const allowFiles = sources.includes('filepicker');
  const isTouch = prefersNativeCapture();
  const captures = isTouch ? [] : availableCaptures(sources, opts.accept, captureSupport());
  if (!captures.length) {
    return allowFiles || isTouch ? openFileDialog(opts) : undefined;
  }

  const choice = await showModalForm<Choice>(({ resolve }) => (
    <SourceChooser opts={opts} allowFiles={allowFiles} captures={captures} resolve={resolve} />
  ));
  if (!choice) {
    return;
  }
  if ('files' in choice) {
    return choice.files;
  }
  const file = await capture(choice.capture);
  return file ? [file] : undefined;
}
